// The form designer's vocabulary: answer kinds, which rules each kind honors in the field app,
// builders for new rows and the checks that run before a save.

// `rules` lists only what the field app actually enforces for that input.
export const KINDS = {
  YesNo: { label: 'بله / خیر', icon: 'toggle', hint: 'دو گزینه، ثبت با یک لمس', rules: [] },
  Triple: { label: 'بله / خیر / ندارد', icon: 'triple', hint: 'سه حالت، برای موارد نامرتبط', rules: [] },
  Score: { label: 'امتیاز ۱ تا ۵', icon: 'star', hint: 'مقیاس ضعیف تا عالی', rules: [] },
  Number: { label: 'عدد', icon: 'number', hint: 'شمارش با دکمه‌های + و −', rules: ['value'] },
  Price: { label: 'مبلغ', icon: 'price', hint: 'به ریال، با جداکننده', rules: ['value'] },
  Input: { label: 'متن کوتاه', icon: 'input', hint: 'یک خط؛ کد، شماره، نام', rules: ['length', 'regex'] },
  Description: { label: 'متن بلند', icon: 'text', hint: 'توضیحات چندخطی', rules: ['length'] },
  RadioChoice: { label: 'تک‌انتخابی', icon: 'radio', hint: 'یک گزینه از چند گزینه', rules: [], choices: 'radio_choices' },
  DropDownList: { label: 'فهرست کشویی', icon: 'dropdown', hint: 'برای فهرست‌های بلند', rules: [], choices: 'dropdown_choices' },
  Multichoice: { label: 'چندانتخابی', icon: 'checks', hint: 'چند گزینه با حداقل و حداکثر', rules: ['count'], choices: 'answer_choices' },
};
export const KIND_ORDER = ['YesNo', 'Triple', 'Score', 'Number', 'Price', 'Input', 'Description', 'RadioChoice', 'DropDownList', 'Multichoice'];
export const CHOICE_FIELDS = ['answer_choices', 'dropdown_choices', 'radio_choices'];

export const RULE_FIELDS = ['is_mandatory', 'mandatory_error_msg_fa', 'min_value', 'max_value', 'value_error_msg_fa',
  'min_lenght', 'max_lenght', 'lenght_error_msg_fa', 'regex', 'regex_error_msg_fa',
  'min_choice_count', 'max_choice_count', 'choice_count_error_msg_fa'];

// Ready patterns for short text answers; each one also fills a Persian error message.
export const REGEX_PRESETS = [
  { label: 'موبایل', regex: '^(\\+98|0)?9\\d{9}$', message: 'شماره موبایل معتبر نیست.' },
  { label: 'کد ملی', regex: '^\\d{10}$', message: 'کد ملی باید ده رقم باشد.' },
  { label: 'کد پستی', regex: '^\\d{10}$', message: 'کد پستی باید ده رقم باشد.' },
  { label: 'تلفن ثابت', regex: '^0\\d{10}$', message: 'شماره تلفن ثابت معتبر نیست.' },
  { label: 'فقط رقم', regex: '^\\d+$', message: 'فقط رقم وارد کنید.' },
  { label: 'ایمیل', regex: '^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$', message: 'ایمیل معتبر نیست.' },
];

let counter = 0;
export const uid = prefix => `${prefix}-${Date.now().toString(36)}-${(counter += 1)}`;

export function emptyRuleFields() {
  return RULE_FIELDS.reduce((fields, name) => ({ ...fields, [name]: name === 'is_mandatory' ? false : null }), {});
}

// Every row gets a client key (`_k`) so lists keep identity while dragging; new questions also
// carry `key`, which photo steps use to link to them before they have an id.
export function prepare(tree) {
  Object.values(tree.tracks || {}).forEach((branch) => {
    (branch.sections || []).forEach((section) => {
      section._k = section._k || (section.id ? `s${section.id}` : uid('s'));
      (section.questions || []).forEach(prepareQuestion);
    });
    (branch.photos || []).forEach((photo) => { photo._k = photo._k || (photo.id ? `p${photo.id}` : uid('p')); });
  });
  if (!tree.tracks.visit) tree.tracks.visit = { sections: [], photos: [] };
  if (tree.kind === 'visit' && !tree.tracks.supervision) tree.tracks.supervision = { sections: [], photos: [] };
  return tree;
}

function prepareQuestion(question) {
  question._k = question._k || (question.id ? `q${question.id}` : question.key || uid('q'));
  if (!question.id && !question.key) question.key = question._k;
  question.choices = question.choices || {};
  CHOICE_FIELDS.forEach((field) => {
    question.choices[field] = (question.choices[field] || []).map(choice => ({ ...choice, _k: choice._k || (choice.id ? `c${choice.id}` : uid('c')) }));
  });
  question.answers = (question.answers || []).map(slot => ({ ...slot, rule: { mode: 'none', preset: null, fields: {}, ...slot.rule } }));
}

export function newQuestion(kindName, types) {
  const type = types.find(item => item.name === kindName);
  const key = uid('q');
  const question = {
    id: null, key, _k: key, text: '', short_text: '', description: '', is_mandatory: true, is_active: true,
    score: null, product: null, has_bool_expected_value: false, bool_unexpected_value: null,
    is_for_decision: false, decision_rank: null, customer_category: '', answered: 0,
    answers: type ? [newSlot(type)] : [],
    choices: { answer_choices: [], dropdown_choices: [], radio_choices: [] },
  };
  const field = KINDS[kindName] && KINDS[kindName].choices;
  if (field) question.choices[field] = [{ id: null, _k: uid('c'), answer: 'گزینه ۱' }, { id: null, _k: uid('c'), answer: 'گزینه ۲' }];
  return question;
}

export function newSlot(type) {
  return { answer_type: type.id, rule: { mode: 'none', preset: null, fields: emptyRuleFields() } };
}

export function newSection(index) {
  return { id: null, _k: uid('s'), name: '', verbose_name: `بخش ${index}`, description: '', is_mandatory: false, is_active: true, report_category: null, questions: [] };
}

export function newPhoto(index) {
  return { id: null, _k: uid('p'), name: '', verbose_name: `مرحله‌ی تصویر ${index}`, description: '', min: 1, max: 3, is_mandatory: true, is_active: true, ai_shelf_enabled: false, report_category: null, question: null, photos: 0 };
}

// A copy is a new row: no ids, fresh keys; choices become new rows too.
export function cloneQuestion(question) {
  const copy = JSON.parse(JSON.stringify(question));
  const key = uid('q');
  Object.assign(copy, { id: null, key, _k: key, answered: 0, text: `${copy.text} (رونوشت)` });
  CHOICE_FIELDS.forEach((field) => { copy.choices[field] = (copy.choices[field] || []).map(choice => ({ id: null, _k: uid('c'), answer: choice.answer })); });
  copy.answers.forEach((slot) => { if (slot.rule.mode === 'custom') slot.rule.preset = null; });
  return copy;
}

export function kindOf(slot, typesById) {
  const type = typesById[slot.answer_type];
  return type ? type.name : '';
}

export function questionRef(question) {
  return question.id || question.key;
}

// Problems block the save (`error`) or only inform (`warning`). Older rows are held to the same
// shape loosely, the same way the server does: new rows must be complete.
export function findProblems(tree, typesById) {
  const problems = [];
  const add = (level, target, message) => problems.push({ level, target, message });
  const form = tree.form || {};
  if (!String(form.verbose_name || '').trim()) add('error', { kind: 'form' }, 'نام فرم خالی است.');
  const isNewRows = Object.values(tree.tracks).some(branch => branch.sections.some(s => !s.id || s.questions.some(q => !q.id)) || branch.photos.some(p => !p.id));
  if (tree.kind === 'visit' && !form.outlet_category && isNewRows) add('error', { kind: 'form' }, 'دسته‌ی فروشگاه این نوع ویزیت انتخاب نشده است.');
  if (tree.kind === 'visit' && !form.has_supervision) {
    const supervision = tree.tracks.supervision || { sections: [], photos: [] };
    if (supervision.sections.length || supervision.photos.length) add('warning', { kind: 'form' }, 'سرکشی خاموش است؛ مراحل سرکشی در اپ نمایش داده نمی‌شوند.');
  }
  const refs = new Set();
  Object.entries(tree.tracks).forEach(([track, branch]) => {
    branch.sections.forEach((section) => {
      if (!String(section.verbose_name || '').trim()) add('error', { kind: 'section', track, item: section }, 'عنوان بخش خالی است.');
      if (section.is_active && !section.questions.some(q => q.is_active)) add('warning', { kind: 'section', track, item: section }, `بخش «${section.verbose_name || 'بی‌نام'}» پرسش فعالی ندارد.`);
      section.questions.forEach((question) => {
        refs.add(questionRef(question));
        const target = { kind: 'question', track, item: question, section };
        const title = question.text ? `«${question.text.slice(0, 40)}»` : 'بی‌متن';
        const isNew = !question.id && !question.copied;
        if (!String(question.text || '').trim()) add('error', target, 'متن پرسش خالی است.');
        if (!question.answers.length) add(isNew ? 'error' : 'warning', target, `پرسش ${title} نوع پاسخ ندارد.`);
        question.answers.forEach((slot) => {
          const kind = KINDS[kindOf(slot, typesById)];
          if (kind && kind.choices && !(question.choices[kind.choices] || []).some(choice => String(choice.answer || '').trim())) {
            add(isNew ? 'error' : 'warning', target, `پرسش ${title} گزینه ندارد.`);
          }
          const labels = (question.choices[kind && kind.choices] || []).map(choice => String(choice.answer || '').trim()).filter(Boolean);
          if (kind && kind.choices && new Set(labels).size !== labels.length) add(isNew ? 'error' : 'warning', target, `پرسش ${title} گزینه‌ی تکراری دارد.`);
          if (slot.rule.mode === 'preset' && !slot.rule.preset) add('error', target, `برای پرسش ${title} قانون آماده انتخاب نشده است.`);
          if (slot.rule.mode === 'custom') {
            const f = slot.rule.fields || {};
            [['min_value', 'max_value', 'مقدار'], ['min_lenght', 'max_lenght', 'طول'], ['min_choice_count', 'max_choice_count', 'تعداد انتخاب']].forEach(([low, high, label]) => {
              if (f[low] !== null && f[low] !== '' && f[high] !== null && f[high] !== '' && Number(f[low]) > Number(f[high])) add('error', target, `در پرسش ${title} حداقل ${label} از حداکثر بیشتر است.`);
            });
            if (f.regex) { try { new RegExp(f.regex); } catch (_) { add('error', target, `الگوی پرسش ${title} معتبر نیست.`); } }
          }
        });
        if (question.has_bool_expected_value && !question.answers.some(slot => ['YesNo', 'Triple'].includes(kindOf(slot, typesById)))) {
          add('warning', target, `هشدار تخلف پرسش ${title} فقط برای پاسخ بله/خیر کار می‌کند.`);
        }
      });
    });
  });
  Object.entries(tree.tracks).forEach(([track, branch]) => {
    branch.photos.forEach((photo) => {
      const target = { kind: 'photo', track, item: photo };
      if (!String(photo.verbose_name || '').trim()) add('error', target, 'عنوان مرحله‌ی تصویر خالی است.');
      if (Number(photo.min) > Number(photo.max)) add('error', target, `در «${photo.verbose_name}» حداقل تصویر از حداکثر بیشتر است.`);
      if (Number(photo.max) < 1) add('error', target, `«${photo.verbose_name}» باید دست‌کم یک تصویر بپذیرد.`);
      if (photo.question && !refs.has(photo.question)) add('warning', target, `«${photo.verbose_name}» به پرسشی بیرون از این فرم وصل است.`);
    });
  });
  return problems;
}

// Server errors name a path such as tracks.visit.sections[1].questions[3].choices.radio_choices.
export function targetFromPath(tree, path) {
  const match = /^tracks\.(\w+)\.(sections|photos)\[(\d+)\](?:\.questions\[(\d+)\])?/.exec(path || '');
  if (!match) return path && path.startsWith('form') ? { kind: 'form' } : null;
  const branch = tree.tracks[match[1]];
  if (!branch) return null;
  if (match[2] === 'photos') return { kind: 'photo', track: match[1], item: branch.photos[Number(match[3])] };
  const section = branch.sections[Number(match[3])];
  if (!section) return null;
  if (match[4] === undefined) return { kind: 'section', track: match[1], item: section };
  return { kind: 'question', track: match[1], item: section.questions[Number(match[4])], section };
}

// One readable line per rule part, in the words the field worker would meet.
export function describeRule(rule) {
  const lines = [];
  const range = (min, max, unit) => {
    if (min != null && max != null) return `${fa(min)} تا ${fa(max)} ${unit}`;
    if (min != null) return `دست‌کم ${fa(min)} ${unit}`;
    return max != null ? `حداکثر ${fa(max)} ${unit}` : '';
  };
  if (rule.is_mandatory) lines.push('پاسخ اجباری');
  const value = range(rule.min_value, rule.max_value, '');
  if (value) lines.push(`مقدار: ${value.trim()}`);
  const length = range(rule.min_lenght, rule.max_lenght, 'نویسه');
  if (length) lines.push(`طول: ${length}`);
  if (rule.regex) lines.push(`الگو: ⁦${rule.regex}⁩`); // isolated, so the pattern reads left to right
  const count = range(rule.min_choice_count, rule.max_choice_count, 'انتخاب');
  if (count) lines.push(`انتخاب: ${count}`);
  return lines;
}

export const fa = value => (value === null || value === undefined || value === '' ? '' : Number(value).toLocaleString('fa-IR'));
